<template>
  <footer class="site-footer">
    <div class="footer-main">
      <div class="container">
        <div class="footer-grid">
          <!-- 公司信息 -->
          <div class="footer-col">
            <h3 class="footer-title">{{ $t('footer.aboutUs') }}</h3>
            <p class="footer-text">
              {{ aboutContent || $t('footer.aboutText') }}
            </p>
            <div class="footer-social">
              <!-- 微信图标 -->
              <a v-if="wechatId" :href="getWechatLink()" class="social-link wechat-link" target="_blank" title="微信">
                <el-icon><ChatDotRound /></el-icon>
              </a>
              <!-- WhatsApp图标 -->
              <a v-if="whatsappNumber" :href="getWhatsappLink()" class="social-link whatsapp-link" target="_blank" title="WhatsApp">
                <el-icon><Phone /></el-icon>
              </a>
              <!-- 联系我们按钮 -->
              <router-link to="/contact" class="social-link contact-link" title="联系我们">
                <el-icon><Message /></el-icon>
              </router-link>
            </div>
          </div>
          <!-- 产品分类 -->
          <div class="footer-col">
            <h3 class="footer-title">{{ $t('footer.products') }}</h3>
            <ul class="footer-links">
              <li v-for="cat in categories" :key="cat.id">
                <router-link :to="{ path: '/products', query: { category: cat.id } }">
                  {{ getCategoryName(cat) }}
                </router-link>
              </li>
            </ul>
          </div>
          <!-- 快速链接 -->
          <div class="footer-col">
            <h3 class="footer-title">{{ $t('footer.quickLinks') }}</h3>
            <ul class="footer-links">
              <li><router-link to="/">{{ $t('nav.home') }}</router-link></li>
              <li><router-link to="/products">{{ $t('products.allProducts') }}</router-link></li>
              <li><router-link to="/about">{{ $t('nav.about') }}</router-link></li>
              <li><router-link to="/contact">{{ $t('nav.contact') }}</router-link></li>
            </ul>
          </div>
          <!-- 联系方式 -->
          <div class="footer-col">
            <h3 class="footer-title">{{ $t('footer.contactInfo') }}</h3>
            <ul class="contact-list">
              <li v-if="contactAddress">
                <el-icon><Location /></el-icon>
                <span>{{ contactAddress }}</span>
              </li>
              <li>
                <el-icon><Phone /></el-icon>
                <span>{{ contactPhone }}</span>
              </li>
              <li>
                <el-icon><Message /></el-icon>
                <span>{{ contactEmail }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <!-- 底部版权 -->
    <div class="footer-bottom">
      <div class="container">
        <p>{{ footerCopyright }}</p>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Location, Phone, Message, ChatDotRound } from '@element-plus/icons-vue'
import { useConfig } from '@/composables/useConfig'
import { useMultiLanguageContent } from '@/composables/useMultiLanguageContent'
import request from '@/api/request'

const currentYear = computed(() => new Date().getFullYear())

// 获取当前语言
const { locale } = useI18n()

// 使用多语言内容管理
const { getCategoryName } = useMultiLanguageContent()

// 产品分类（从后端动态获取，只显示首页显示的分类）
const categories = ref([])

// 加载分类数据
const loadCategories = async () => {
  try {
    const response = await request.get('/categories/all')
    if (response.code === 200) {
      // 只显示一级分类且在首页显示的分类
      categories.value = (response.data || []).filter(c => c.level === 1 && c.showOnHome)
      console.log('Footer categories loaded:', categories.value) // 调试日志
    } else {
      console.error('Failed to load categories:', response)
      categories.value = [] // 确保即使响应失败也有一个空数组
    }
  } catch (error) {
    console.error('加载分类列表失败:', error)
    categories.value = [] // 确保即使发生异常也有一个空数组
  }
}

onMounted(() => {
  loadCategories()
})

// 监听语言变化，重新加载分类（分类名称会自动更新）
watch(locale, async () => {
  // 语言变化时，重新加载分类
  await loadCategories()
})

// 从后端配置获取联系信息和版权信息
const { getConfig, loading: configLoading } = useConfig()
const contactAddress = computed(() => getConfig('contact.address', ''))
const contactPhone = computed(() => getConfig('contact.phone', '+86-769-8888-8888'))
const contactEmail = computed(() => getConfig('contact.email', 'info@caihui-printing.com'))
const footerCopyright = computed(() => getConfig('footer.copyright', `© ${currentYear.value} Dongguan Caicheng Printing Factory. All rights reserved.`))
const aboutContent = computed(() => getConfig('about.content', ''))

// 聊天联系方式
const wechatId = computed(() => getConfig('contact.wechat', ''))
const whatsappNumber = computed(() => getConfig('contact.whatsapp', ''))

// 生成微信链接（可以是二维码图片或微信号）
const getWechatLink = () => {
  if (!wechatId.value) return '#'
  // 如果是URL，直接返回
  if (wechatId.value.startsWith('http://') || wechatId.value.startsWith('https://')) {
    return wechatId.value
  }
  // 否则尝试调用微信客户端（weixin://协议）
  return `weixin://${wechatId.value}`
}

// 生成WhatsApp链接
const getWhatsappLink = () => {
  if (!whatsappNumber.value) return '#'
  // 移除号码中的非数字字符
  const cleanNumber = whatsappNumber.value.replace(/[^0-9]/g, '')
  // 使用wa.me链接，会自动调用WhatsApp客户端
  return `https://wa.me/${cleanNumber}`
}

// 监听配置加载完成事件
watch(configLoading, async (loading) => {
  if (!loading) {
    // 配置加载完成后，重新加载分类
    await loadCategories()
  }
})
</script>

<style scoped>
.site-footer {
  background-color: var(--color-bg-dark);
  color: #ccc;
}

.footer-main {
  padding: 48px 0 32px;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.5fr;
  gap: 40px;
}

.footer-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-white);
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 12px;
}

.footer-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 40px;
  height: 2px;
  background-color: var(--color-primary);
}

.footer-text {
  font-size: 13px;
  line-height: 1.8;
  margin-bottom: 16px;
}

.footer-social {
  display: flex;
  gap: 12px;
}

.social-link {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  transition: all 0.3s ease;
}

.social-link:hover {
  background-color: var(--color-primary);
  color: var(--color-white);
}

.social-link.wechat-link:hover {
  background-color: #07C160;
}

.social-link.whatsapp-link:hover {
  background-color: #25D366;
}

.social-link.contact-link:hover {
  background-color: var(--color-secondary);
}

.footer-links li {
  margin-bottom: 10px;
}

.footer-links a {
  font-size: 13px;
  color: #aaa;
  transition: color 0.3s ease;
}

.footer-links a:hover {
  color: var(--color-white);
}

.contact-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 13px;
}

.contact-list .el-icon {
  margin-top: 2px;
  color: var(--color-primary);
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 16px 0;
  text-align: center;
  font-size: 12px;
  color: #888;
}

@media (max-width: 768px) {
  .footer-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}
</style>